import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';

const emptyAward = {
  employee_id: '',
  employee_name: '',
  award_type: 'recognition',
  title: '',
  description: '',
  month: new Date().getMonth() + 1,
  year: new Date().getFullYear(),
  points: 50,
  badge: 'star',
};

export function Awards() {
  const { t } = useApp();
  const [summary, setSummary] = useState(null);
  const [awards, setAwards] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [awardsLoadFailed, setAwardsLoadFailed] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyAward);

  async function load() {
    setAwardsLoadFailed(false);
    let s = null;
    let a = [];
    let e = [];
    try {
      const res = await api.get('/api/dashboard/summary');
      s = res.data;
    } catch (error) {
      console.error('Awards page dashboard load error', error);
    }
    try {
      const res = await api.get('/api/awards');
      a = Array.isArray(res.data) ? res.data : [];
    } catch (error) {
      console.error('Awards load error', error);
      setAwardsLoadFailed(true);
      a = [];
    }
    try {
      const res = await api.get('/api/employees');
      e = Array.isArray(res.data) ? res.data : [];
    } catch (error) {
      console.error('Awards page employees load error', error);
      e = [];
    }
    setSummary(s);
    setAwards(a);
    setEmployees(e);
  }

  useEffect(() => {
    load();
  }, []);

  async function save() {
    try {
      await api.post('/api/awards', {
        ...form,
        month: Number(form.month),
        year: Number(form.year),
        points: Number(form.points),
      });
      setModal(false);
      setForm(emptyAward);
      await load();
    } catch (error) {
      console.error('Awards save error', error);
    }
  }

  const eom = summary?.employee_of_month;

  return (
    <div className="page-stack page-stack--awards">
      <section className="awards-hero">
        <h2 className="awards-hero__title">{t('awards.heroTitle')}</h2>
        <p className="awards-hero__lead">{t('awards.heroLead')}</p>
        <p className="awards-hero__culture">{t('awards.cultureLine')}</p>
      </section>

      <p className="page-lead page-lead--tight">{t('awards.subtitle')}</p>

      <div className="categories-row" aria-label={t('awards.categoriesTitle')}>
        <span className="category-pill">{t('awards.catInnovation')}</span>
        <span className="category-pill">{t('awards.catAttitude')}</span>
        <span className="category-pill">{t('awards.catTeamwork')}</span>
        <span className="category-pill">{t('awards.catPerformance')}</span>
        <span className="category-pill">{t('awards.catLeadership')}</span>
      </div>

      {eom && eom.name && (
        <section className="eom-card eom-card--premium eom-card--featured">
          <div className="eom-card__content">
            <h2 className="eom-card__kicker">{t('awards.employeeMonth')}</h2>
            <h3 className="eom-card__name">{eom.name}</h3>
            <p className="text-muted">{eom.area ?? ''}</p>
            <div className="eom-card__badges" aria-hidden>
              <span className="visual-badge">★</span>
              <span className="visual-badge visual-badge--alt">✦</span>
              <span className="visual-badge">◎</span>
            </div>
          </div>
          <div className="eom-card__badge" aria-hidden>
            ★
          </div>
        </section>
      )}

      <div className="toolbar toolbar--card">
        <h2 className="panel__title inline-title">{t('awards.recent')}</h2>
        <div className="toolbar__grow" />
        <button type="button" className="btn btn--primary" onClick={() => setModal(true)}>
          {t('awards.new')}
        </button>
      </div>

      <div className="awards-grid">
        {awardsLoadFailed && awards.length === 0 ? (
          <div className="empty-state">{t('awards.empty')}</div>
        ) : awards.length === 0 ? (
          <div className="empty-state">{t('awards.empty')}</div>
        ) : (
          awards.map((aw) => (
            <article key={aw.id} className="award-card">
              <div className="award-card__points">
                +{aw.points ?? 0} {t('awards.points')}
              </div>
              <h3>{aw.title ?? '—'}</h3>
              <p className="text-muted">{aw.employee_name ?? '—'}</p>
              <p className="text-sm">{aw.description ?? ''}</p>
              <span className="chip">{aw.badge || aw.award_type || '—'}</span>
            </article>
          ))
        )}
      </div>

      {modal && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal">
            <h2 className="modal__title">{t('awards.new')}</h2>
            <div className="form-grid">
              <label className="field field--full">
                <span>Colaborador</span>
                <select
                  className="input"
                  value={form.employee_id}
                  onChange={(e) => {
                    const emp = employees.find((x) => x.id === e.target.value);
                    setForm({
                      ...form,
                      employee_id: e.target.value,
                      employee_name: emp ? `${emp.first_name} ${emp.last_name}` : '',
                    });
                  }}
                >
                  <option value="">—</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field field--full">
                <span>Título</span>
                <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </label>
              <label className="field field--full">
                <span>Descripción</span>
                <input
                  className="input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Puntos</span>
                <input
                  className="input"
                  type="number"
                  value={form.points}
                  onChange={(e) => setForm({ ...form, points: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Badge</span>
                <input className="input" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} />
              </label>
            </div>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setModal(false)}>
                {t('employees.cancel')}
              </button>
              <button type="button" className="btn btn--primary" onClick={save} disabled={!form.employee_id}>
                {t('employees.save')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
